'use client';

import { useEffect } from 'react';
import { usePathname, useRouter } from '@/lib/app-router';
import { useAuthStore } from '@/stores/user.store';
import { useThemeStore } from '@/stores/theme.store';
import { UserService } from '@/services/user.service';
import { computeRequiresAuth } from '@/lib/constants/auth.constants';
import { useLoginHref } from '@/hooks/useLoginHref';
import { logger } from '@/lib/logger';

export function ClientRouteGuardProvider({
  children,
  refreshFailed,
}: {
  children: React.ReactNode;
  refreshFailed: boolean;
}) {
  const pathname = usePathname();
  const router = useRouter();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isAuthInitialized = useAuthStore((state) => state.isAuthInitialized);
  const disableGuestBrowsing = useThemeStore(
    (state) => state.theme?.disableGuestBrowsing ?? false
  );

  const requiresAuth = computeRequiresAuth(pathname, disableGuestBrowsing);

  /**
   * Login destination for both redirects below, carrying the page being left.
   *
   * ## Invariants:
   * - Carries a `returnUrl` rather than a bare `/login` (PB-4971). Why: with
   *   `disableGuestBrowsing` set, this guard fires on EVERY route — scan and touchless surfaces
   *   included — so a bare redirect drops a parker who scanned a garage sign onto /myaccount, and
   *   the QR code that would let them start over is on the sign, not on their phone.
   * - The `returnUrl` keeps the QUERY STRING, which is where a scan carries its company and
   *   direction (`/z/412?c=ACME&d=i`); a bare path is a url the scan cannot resolve a company from.
   */
  const loginHref = useLoginHref();

  // Handle refresh failure on initial load
  useEffect(() => {
    if (!refreshFailed) return;

    const cleanup = async () => {
      try {
        const userService = new UserService();
        await userService.deleteRefreshTokenFromCookie();
      } catch (error) {
        logger.error('Failed to delete auth cookies after refresh failure', error, {
          feature: 'route-guard',
          route: 'cleanup',
        });
      }

      if (requiresAuth) {
        router.replace(loginHref);
      }
    };

    cleanup();
    // pathname kept in deps so the cleanup re-fires across navigations after
    // a refresh failure, preserving the original effect's re-run semantics.
  }, [refreshFailed, requiresAuth, pathname, router, loginHref]);

  // Route protection on pathname/auth changes (SPA navigation) and on cold load.
  useEffect(() => {
    // Wait until HydrateStoreProvider has resolved the session (via the SSR
    // refresh-token exchange). Before that, isAuthenticated is not yet
    // trustworthy, so redirecting would bounce a genuinely-authenticated user.
    // Gating on isAuthInitialized (instead of skipping the first render) means a
    // cold load of a protected route with no recoverable session redirects to
    // login once boot resolves, rather than hanging on the full-screen loader
    // when the refresh exchange fails softly (non-401/500). (PB-3288)
    if (!isAuthInitialized) return;

    if (requiresAuth && !isAuthenticated) {
      router.replace(loginHref);
    }
  }, [isAuthInitialized, requiresAuth, isAuthenticated, router, loginHref]);

  return <>{children}</>;
}
